import type { CustomWidgetDescription } from '../../../management/customDashboard/CustomWidgetDescription.types.ts';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { VolatilityTileContentContainer } from './VolatilityTile.tsx';
import { DailyReturnsTileContent } from './DailyReturnsTile.tsx';
import { useTwrEquityWidgetInputSuspenseQuery } from '../../../../generated/graphql.tsx';
import { UserSettings } from '../../../management/UserSettings.types.ts';
import { getSavedDaysRange, useTwrEquityData } from './PortfolioTilesService.ts';
import { TwrTileContent } from './TwrTile.tsx';
import { EquityTileContent } from './EquityTile.tsx';
import { useWidgetState } from 'components/management/customDashboard/CustomWidgetContext.tsx';
import {
  PortfolioTileSettings,
  type PortfolioTileSettingsState,
} from 'components/portfolio/dashboard/tiles/PortfolioTileSettings.tsx';

export const portfolioTileWidgets: CustomWidgetDescription[] = [
  {
    name: 'Equity value',
    type: 'portfolioEquityTile',
    description: 'See your portfolio equity',
    icon: () => <CreditCardIcon />,
    viewComponent: ({ id }) => {
      const { data } = useTwrEquityWidgetInputSuspenseQuery({
        variables: { tileSettings: UserSettings.TilesDateRange },
      });

      const initialState = useWidgetState<PortfolioTileSettingsState>(id);

      const { savedTimeFrameInDays, savedDateRange } = getSavedDaysRange(data);
      const { twr, loading } = useTwrEquityData({ savedTimeFrameInDays, ...initialState });

      return <EquityTileContent hideTitle loading={loading} twr={twr} since={savedDateRange.value()![0]} />;
    },
    settingsComponent: PortfolioTileSettings,
    initialH: 4,
    initialW: 3,
  },
  {
    name: 'TWR',
    type: 'portfolioTwrTile',
    description: 'See your portfolio twr',
    icon: () => <CreditCardIcon />,
    viewComponent: ({ id }) => {
      const { data } = useTwrEquityWidgetInputSuspenseQuery({
        variables: { tileSettings: UserSettings.TilesDateRange },
      });

      const initialState = useWidgetState<PortfolioTileSettingsState>(id);

      const { savedTimeFrameInDays, savedDateRange } = getSavedDaysRange(data);
      const { twr, twrSince, loading } = useTwrEquityData({
        savedTimeFrameInDays,
        ...initialState,
      });

      return (
        <TwrTileContent hideTitle dateRangeLabel={savedDateRange.label} loading={loading} twr={twr} since={twrSince} />
      );
    },
    settingsComponent: PortfolioTileSettings,
    initialH: 4,
    initialW: 3,
  },
  {
    name: 'Last 30-days returns',
    type: 'portfolioDailyReturnTile',
    description: 'See your daily returns',
    icon: () => <CreditCardIcon />,
    viewComponent: ({ id }) => {
      const initialState = useWidgetState<PortfolioTileSettingsState>(id);
      return <DailyReturnsTileContent hideTitle {...initialState} />;
    },
    settingsComponent: PortfolioTileSettings,
    initialH: 4,
    initialW: 3,
  },
  {
    name: 'Volatility',
    type: 'portfolioVolatilityTile',
    description: 'See your 30 days volatility',
    icon: () => <CreditCardIcon />,
    viewComponent: ({ id }) => {
      const initialState = useWidgetState<PortfolioTileSettingsState>(id);
      return <VolatilityTileContentContainer hideTitle {...initialState} />;
    },
    settingsComponent: PortfolioTileSettings,
    initialState: { title: 'Volatility' },
    initialH: 4,
    initialW: 3,
  },
];
